import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getModuleStartSlug, NUMEROLOGY_COURSE, NUMEROLOGY_COURSE_APPENDIX } from '../../data/numerologyCourse';
import '../../components/numerology/numerologyCourse.css';

const PAGE_TITLE = 'Numerology Course | Astral Moon Magic';

function lessonHref(lesson) {
  return lesson.type === 'route' ? lesson.to : `/numerology/study/${lesson.slug}`;
}

export default function NumerologyLearnLandingPage() {
  useEffect(() => {
    const prev = document.title;
    document.title = PAGE_TITLE;
    return () => {
      document.title = prev;
    };
  }, []);

  const m1 = NUMEROLOGY_COURSE[0];
  const m1Start = getModuleStartSlug(m1);
  const module1Entry = m1Start ? `/numerology/study/${m1Start}` : `/numerology/study/${m1.path}`;

  return (
    <>
      <section className="amm-num-course-landing__intro" aria-labelledby="course-title">
        <p className="amm-kicker">Course</p>
        <h1 id="course-title" className="amm-num-course-landing__title">
          Numerology course
        </h1>
        <p className="amm-num-course-landing__subtitle">
          Seven modules take you from foundations and core numbers through number meanings, timing, life cycles,
          and a full reading framework — one ordered path you can follow or revisit by module.
        </p>
        <p className="amm-num-course-landing__progress">
          <span className="amm-num-course-landing__progress-label">How the modules progress</span>
          Foundations and core numbers first, then shared digit language, secondary positions, personal timing, life
          cycles, and finally synthesis with samples. Each block below lists its lessons; open a module for the full
          in-order list and navigation on that module&apos;s page.
        </p>
      </section>

      <section
        id="modules"
        className="amm-num-course-landing__modules"
        aria-labelledby="modules-heading"
      >
        <h2 id="modules-heading" className="amm-num-course-landing__modules-heading">
          Course structure
        </h2>
        <div className="amm-num-course-landing__module-grid">
          {NUMEROLOGY_COURSE.map((mod) => (
            <article key={mod.id} className="amm-num-course-landing__module-card">
              <header className="amm-num-course-landing__module-card-head">
                <p className="amm-num-course-landing__module-card-eyebrow">Module {mod.number}</p>
                <h3 className="amm-num-course-landing__module-card-title">{mod.title}</h3>
              </header>
              <p className="amm-num-course-landing__module-card-desc">{mod.description}</p>

              <details className="amm-num-course-landing__lessons-details">
                <summary className="amm-num-course-landing__lessons-summary">
                  Lessons
                  <span className="amm-num-course-landing__lessons-count">
                    ({mod.lessons.length}
                    {mod.optionalLessons?.length ? ` + ${mod.optionalLessons.length} optional` : ''})
                  </span>
                </summary>
                <ul className="amm-num-course-landing__lessons-list">
                  {mod.lessons.map((l) => (
                    <li key={l.slug || l.to}>
                      <Link className="amm-num-course-landing__lesson-link" to={lessonHref(l)}>
                        {l.label}
                      </Link>
                    </li>
                  ))}
                </ul>
                {mod.optionalLessons?.length ? (
                  <>
                    <p className="amm-num-course-landing__optional-label">Optional</p>
                    <ul className="amm-num-course-landing__lessons-list amm-num-course-landing__lessons-list--optional">
                      {mod.optionalLessons.map((l) => (
                        <li key={l.slug}>
                          <Link className="amm-num-course-landing__lesson-link" to={`/numerology/study/${l.slug}`}>
                            {l.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </>
                ) : null}
              </details>

              <div className="amm-num-course-landing__module-card-actions">
                <Link className="amm-button-secondary" to={`/numerology/study/${mod.path}`}>
                  Open module
                </Link>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="amm-num-course-landing__start" aria-labelledby="start-heading">
        <h2 id="start-heading" className="amm-num-course-landing__start-title">
          When you are ready
        </h2>
        <p className="amm-num-course-landing__start-lead">
          Start at Module 1 if you are new; each module page lists lessons in order with clear next steps.
        </p>
        <div className="amm-num-course-landing__start-row">
          <Link className="amm-button" to={module1Entry}>
            Begin with foundations
          </Link>
          <Link className="amm-button-secondary" to="/numerology/study#modules">
            View modules
          </Link>
        </div>
      </section>

      <section className="amm-num-course-landing__appendix" aria-labelledby="appendix-heading">
        <h2 id="appendix-heading">Appendix</h2>
        <p>
          One reference sheet for letter values, positions, and digit handles — for when you are practising or
          skimming a report, not a substitute for the modules. The course path above remains the source of method and
          depth.
        </p>
        <ul className="amm-list">
          {NUMEROLOGY_COURSE_APPENDIX.map((item) => (
            <li key={item.slug}>
              <Link className="amm-text-link" to={`/numerology/study/${item.slug}`}>
                {item.label}
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <div className="amm-num-course-landing__report-note">
        <strong>Personal report (separate)</strong> — The generated chart report is its own product: apply your details
        when you want a written synthesis. It does not replace these lessons, and you can complete the course without
        it.{' '}
        <Link className="amm-text-link" to="/numerology/report">
          Open report generator
        </Link>
      </div>
    </>
  );
}
